import { ArrowRight } from "lucide-react";
import type { CtaBannerComponent } from "../../types";
import { GuardedLink } from "../../runtime/DevPlaceholder";

/**
 * Call-to-action banner.
 *
 * Two visual variants — picked via `content.variant`:
 *
 *  - **`soft`** (default, kept for back-compat) — quiet light-blue
 *    (`#F3F7FC`) card, brand-blue heading, inline text link with a small
 *    yellow arrow. Lives inside editorial body flow.
 *
 *  - **`primary`** — full-width brand-blue (`#005CA9`) banner with white
 *    heading and description copy, plus a rounded yellow (`#FFA401`)
 *    button. Used as a section's main call-to-action (e.g. "discover all
 *    the governors" under the organes-de-la-bcc card grid).
 *
 * Colours come straight from the BCC palette: `#005CA9` brand blue,
 * `#FFA401` accent yellow, white text on the dark variant. No new
 * design tokens, no per-page CSS — the renderer stays "dumb" and just
 * lays out CMS-provided strings.
 */
export function CtaBannerRenderer({ component }: { component: CtaBannerComponent }) {
  const { title, description, linkLabel, linkHref, variant = "soft", linkLabelWhite } = component.content;
  // Sur le bandeau primary (fond bleu, bouton ambre) le libellé du bouton est
  // BLANC par défaut — `linkLabelWhite: false` reste l'opt-out pour un libellé
  // sombre au cas par cas.
  const primaryLinkTextClass =
    linkLabelWhite === false || String(linkLabelWhite) === "false" ? "text-[#0F2849]" : "text-white";

  if (variant === "primary") {
    return (
      <section className="mt-12 mb-8 md:mt-16 md:mb-10">
        <div className="rounded-md bg-[#005CA9] px-6 py-7 md:px-10 md:py-10">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between md:gap-8">
            <div className="flex flex-col gap-2 md:max-w-2xl">
              <h3 className="text-xl md:text-2xl font-semibold text-white">{title}</h3>
              {description ? (
                <p className="text-sm md:text-base text-white/85 leading-relaxed">{description}</p>
              ) : null}
            </div>
            {linkLabel ? (
              <GuardedLink
                href={linkHref}
                label={linkLabel}
                className={`inline-flex shrink-0 items-center gap-2 self-start md:self-auto rounded-full bg-[#FFA401] px-6 py-2.5 text-sm md:text-base font-semibold ${primaryLinkTextClass} transition hover:bg-[#ff9200] focus:outline-none focus:ring-2 focus:ring-white/50`}
              >
                {linkLabel}
                <ArrowRight className="h-4 w-4 shrink-0" />
              </GuardedLink>
            ) : null}
          </div>
        </div>
      </section>
    );
  }

  return (
    // Top spacing so the quiet variant never sits flush against the block above
    // it (card grids in particular ran straight into the panel).
    <section className="mt-10 md:mt-12">
      <div className="rounded-md bg-[#F3F7FC] px-6 py-5 md:px-8 md:py-6">
        <div className="flex flex-col gap-2">
          <h3 className="text-lg xl:text-xl font-semibold text-[#005CA9] md:text-xl">{title}</h3>
          {description ? (
            <p className="text-sm md:text-base text-[#0F2849]/80 leading-relaxed">{description}</p>
          ) : null}
          {linkLabel ? (
            <GuardedLink href={linkHref} label={linkLabel} className="inline-flex items-center gap-2 text-sm xl:text-base font-normal text-[#005CA9]">
              {linkLabel}
              <ArrowRight className="h-4 w-4 shrink-0 text-[#FFA401]" />
            </GuardedLink>
          ) : null}
        </div>
      </div>
    </section>
  );
}
